import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  AlertCircle,
  CheckCircle,
  FileText,
  Loader2,
  Upload,
  X
} from "lucide-react";
import { societesService, depotsService } from "@/services";
import type { Societe } from "@/types";

interface UploadFile {
  file: File;
  id: string;
  status: "pending" | "uploading" | "processing" | "completed" | "error";
  progress: number;
  candidateName?: string;
  error?: string;
  extractedText?: string;
}

interface CVImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appelOffreId: string | undefined;
  onImportComplete?: () => void;
}

export function CVImportDialog({ open, onOpenChange, appelOffreId, onImportComplete }: CVImportDialogProps) {
  const { toast } = useToast();
  const [societes, setSocietes] = useState<Societe[]>([]);
  const [loadingSocietes, setLoadingSocietes] = useState(false);
  const [selectedSocieteId, setSelectedSocieteId] = useState<string>("");
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setIsDragOver(false);
      return;
    }

    loadSocietes();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedSocieteId("");
    }
  }, [open]);

  const loadSocietes = async () => {
    setLoadingSocietes(true);
    try {
      const data = await societesService.getAll();
      setSocietes(data);
    } catch (error) {
      console.error("Erreur lors du chargement des sociétés:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger la liste des sociétés",
        variant: "destructive"
      });
    } finally {
      setLoadingSocietes(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiles = event.target.files ? Array.from(event.target.files) : [];
    if (inputFiles.length === 0) return;
    addFiles(inputFiles);
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length === 0) return;
    addFiles(droppedFiles);
  };

  const addFiles = (newFiles: File[]) => {
    if (!appelOffreId) {
      toast({
        title: "Appel d'offres manquant",
        description: "Impossible d'importer sans appel d'offres sélectionné",
        variant: "destructive"
      });
      return;
    }

    if (!selectedSocieteId) {
      toast({
        title: "Sélection requise",
        description: "Veuillez sélectionner une société avant d'importer",
        variant: "destructive"
      });
      return;
    }

    const validFiles = newFiles.filter((file) => {
      const extension = file.name.toLowerCase();
      const isValidType = extension.endsWith(".pdf") || extension.endsWith(".docx");
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10 MB

      if (!isValidType) {
        toast({
          title: "Format non supporté",
          description: `${file.name} n'est pas au format PDF ou DOCX`,
          variant: "destructive"
        });
        return false;
      }

      if (!isValidSize) {
        toast({
          title: "Fichier trop volumineux",
          description: `${file.name} dépasse la taille maximale de 10MB`,
          variant: "destructive"
        });
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    const uploads = validFiles.map((file) => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      status: "pending" as const,
      progress: 0
    }));

    setFiles((previous) => [...previous, ...uploads]);

    // Lancer automatiquement le traitement des fichiers valides
    uploads.forEach((upload) => {
      void processFile(upload);
    });
  };

  const processFile = async (uploadFile: UploadFile) => {
    if (!appelOffreId || !selectedSocieteId) return;

    try {
      setFiles((prev) =>
        prev.map((file) =>
          file.id === uploadFile.id
            ? { ...file, status: "uploading", progress: 25 }
            : file
        )
      );

      setFiles((prev) =>
        prev.map((file) =>
          file.id === uploadFile.id
            ? { ...file, status: "processing", progress: 50 }
            : file
        )
      );

      // Appel API unifié qui gère upload, extraction et création
      const result = await depotsService.importCV(
        appelOffreId,
        selectedSocieteId,
        uploadFile.file,
      );

      setFiles((prev) =>
        prev.map((file) =>
          file.id === uploadFile.id
            ? { ...file, progress: 75, candidateName: result.candidatName }
            : file
        )
      );

      setFiles((prev) =>
        prev.map((file) =>
          file.id === uploadFile.id
            ? {
                ...file,
                status: "completed",
                progress: 100,
                extractedText: result.extractedText
              }
            : file
        )
      );

      toast({
        title: "Document importé",
        description: `${result.candidatName} - ${uploadFile.file.name}`
      });

      onImportComplete?.();
    } catch (error) {
      console.error("Erreur lors du traitement du fichier:", error);
      setFiles((prev) =>
        prev.map((file) =>
          file.id === uploadFile.id
            ? {
                ...file,
                status: "error",
                progress: 0,
                error: error instanceof Error ? error.message : "Erreur inconnue"
              }
            : file
        )
      );

      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Impossible de traiter le document",
        variant: "destructive"
      });
    }
  };

  const removeFile = (fileId: string) => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const renderStatusBadge = (status: UploadFile["status"]) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary">En attente</Badge>;
      case "uploading":
        return <Badge className="bg-accent text-accent-foreground">Upload...</Badge>;
      case "processing":
        return <Badge className="bg-accent text-accent-foreground">Extraction...</Badge>;
      case "completed":
        return <Badge className="bg-success text-success-foreground">Terminé</Badge>;
      case "error":
        return <Badge variant="destructive">Erreur</Badge>;
    }
  };

  const renderStatusIcon = (status: UploadFile["status"]) => {
    switch (status) {
      case "pending":
        return <FileText className="h-4 w-4 text-muted-foreground" />;
      case "uploading":
        return <Upload className="h-4 w-4 text-primary animate-pulse" />;
      case "processing":
        return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-success" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-destructive" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Importer des documents</DialogTitle>
          <DialogDescription>
            Sélectionnez la société puis déposez vos CV pour lancer automatiquement l'extraction Vision Infomaniak et l'enregistrement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="societe-select">Société</Label>
            <Select
              value={selectedSocieteId}
              onValueChange={setSelectedSocieteId}
              disabled={loadingSocietes}
            >
              <SelectTrigger id="societe-select">
                <SelectValue placeholder={loadingSocietes ? "Chargement..." : "Sélectionner une société"} />
              </SelectTrigger>
              <SelectContent>
                {societes.map((societe) => (
                  <SelectItem key={societe.id} value={societe.id}>
                    {societe.nom}
                    {societe.localite ? ` (${societe.localite})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setIsDragOver(false);
            }}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragOver ? "border-primary bg-primary/5" : "border-border"
            } ${!selectedSocieteId ? "opacity-60" : ""}`}
          >
            <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-2">
              Glissez vos fichiers ici ou sélectionnez-les depuis votre ordinateur
            </p>
            <p className="text-xs text-muted-foreground mb-4">Formats acceptés: PDF ou DOCX · 10MB max</p>
            <div>
              <label className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md cursor-pointer hover:bg-primary/90 transition-colors">
                Choisir des fichiers
                <Input
                  type="file"
                  multiple
                  accept=".pdf,.docx"
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={!selectedSocieteId}
                  capture={undefined}
                />
              </label>
            </div>
            {!selectedSocieteId && (
              <p className="mt-3 text-xs text-destructive">
                Sélectionnez une société pour activer l'import automatique.
              </p>
            )}
          </div>

          {files.length > 0 && (
            <div className="space-y-3">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="border rounded-lg p-3 bg-muted/40"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      {renderStatusIcon(file.status)}
                      <div>
                        <p className="font-medium text-sm">{file.file.name}</p>
                        {file.candidateName && (
                          <p className="text-xs text-muted-foreground">Candidat détecté: {file.candidateName}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {renderStatusBadge(file.status)}
                      {(file.status === "pending" || file.status === "error") && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => removeFile(file.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {(file.status === "uploading" || file.status === "processing") && (
                    <div className="mt-2 space-y-1">
                      <Progress value={file.progress} className="h-2" />
                      <p className="text-xs text-muted-foreground">
                        {file.status === "uploading" ? "Upload en cours" : "Extraction du texte"}
                      </p>
                    </div>
                  )}

                  {file.status === "error" && file.error && (
                    <p className="mt-2 text-xs text-destructive">{file.error}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fermer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
